<template>
  <!--언어 선택-->
  <div class="contents-wrap">
    <p class="small semibold">{{ t('entrance_intro_message_1') }}</p>
    <div
      ref="triggerLanRef"
      class="dropdown-trigger"
      :class="{
        active: showDropdown,
        'input-error': isVisibleErrorLanguage,
      }"
      @click="toggleDropdown"
    >
      <p>
        <template v-if="languageSelected">
          {{ languageSelected.name }} - {{ t(`label_language_${languageSelected.code}`) }}
        </template>
        <templage v-else>
          {{ t('choose_language') }}
        </templage>
      </p>

      <component :is="showDropdown ? ChevronUpIcon : ChevronDownIcon" class="icon-medium icon-default icon-dropdown" />
    </div>

    <div v-if="isVisibleErrorLanguage" class="error-message">
      <InfoIcon class="icon-smaller icon-error" />
      <p class="small">{{ t('error_input_no_language') }}</p>
    </div>

    <Dropdown
      v-if="showDropdown"
      position="below"
      :trigger-ref="triggerLanRef"
      :class="{ active: showDropdown }"
      @close="closeDropdown"
    >
      <LanguageList
        :languages="languageStore.interpretationLanguageOptions.textOutputEnabled"
        :showAllInList="true"
        :selected-at-top="true"
        is-using-language-detection
        @select="changeLanguage"
      />
    </Dropdown>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ChevronDownIcon from '@/components/icons/IconChevronDown.vue';
import ChevronUpIcon from '@/components/icons/IconChevronUp.vue';
import InfoIcon from '@/components/icons/IconInfo.vue';
import Dropdown from '@/components/shared/base/Dropdown.vue';
import LanguageList from '@/components/shared/base/LanguageList.vue';
import { useAudioStore } from '@/stores/audio';
import { useLanguageStore } from '@/stores/language';
import { TypeCall } from '@/types/common';
import { Language } from '@/types/components';

const { t } = useI18n();

const audioStore = useAudioStore();
const languageStore = useLanguageStore();

const languageSelected = defineModel<Language>('languageSelected');
const isVisibleErrorLanguage = defineModel('isVisibleErrorLanguage');

withDefaults(
  defineProps<{
    chatType: TypeCall;
  }>(),
  {
    chatType: 'GENERAL',
  }
);

const showDropdown = ref(false);
const triggerLanRef = ref();
const dropdownContainerRef = ref<HTMLElement>();

const changeLanguage = (language) => {
  isVisibleErrorLanguage.value = false;
  languageSelected.value = language;

  audioStore.selectLanguage(language.code);

  showDropdown.value = false;
};

const closeDropdown = () => {
  showDropdown.value = false;
};

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;

  if (showDropdown.value) {
    document.addEventListener('click', handleClickOutside);
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
};

const handleClickOutside = (event) => {
  if (dropdownContainerRef.value && !dropdownContainerRef.value.contains(event.target)) {
    closeDropdown();
  }
};
</script>

<style scoped lang="scss">
.contents-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.25rem;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  white-space: nowrap;
}

.dropdown-trigger {
  display: flex;
  width: 100%;
  gap: 0.5rem;
  justify-content: center;
  padding: 0.75rem;
  background-color: var(--surface-default);
  border-radius: 8px;
  cursor: pointer;
}

.dropdown-trigger:hover,
.dropdown-trigger:active,
.dropdown-trigger.active {
  background-color: var(--surface-gray-dim);
}

::v-deep(.dropdown-content) {
  width: 77dvw;
  max-width: 21rem;
}

.icon-dropdown {
  position: absolute;
  right: 1rem;
  flex-shrink: 0;
}

.error-message {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  fill: var(--error);
  color: var(--error);
  gap: 0.25rem;
}
</style>
